// The effective form document, schema version 1, as the API materialises
// it (app.services.effective_form). Parsed from the stored JSON with the
// checks the renderer needs; an unknown field type is kept as text and
// rendered as a visible marker, never dropped.
import type { Bands, PhysicalRange } from './semaphore';

export const SUPPORTED_SCHEMA_VERSION = 1;

export const KNOWN_FIELD_TYPES = ['numeric', 'text', 'check', 'photo', 'select', 'date'] as const;
export type KnownFieldType = (typeof KNOWN_FIELD_TYPES)[number];

export interface FormOption {
  code: string;
  label: string;
}

export interface FormField extends PhysicalRange {
  field_definition_id: string;
  code: string;
  name: string;
  /** One of the six known types, or whatever the document says. */
  field_type: string;
  unit: string | null;
  decimals: number | null;
  options: FormOption[];
  is_required: boolean;
  requires_observation_on_fail: boolean;
  requires_photo_on_fail: boolean;
  limit_display: string | null;
  bands: Bands & { criticality: string };
  origin: string;
}

export interface FormGroup {
  id: string;
  name: string;
  sort_order: number;
  min_photos: number;
  fields: FormField[];
}

export interface FormDocument {
  schema_version: number;
  site_id: string;
  template_version_id: string;
  computed_at: string;
  content_hash: string;
  groups: FormGroup[];
}

export class FormDocumentError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'FormDocumentError';
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function text(record: Record<string, unknown>, key: string): string {
  const value = record[key];
  if (typeof value !== 'string') throw new FormDocumentError(`${key} must be text`);
  return value;
}

function optionalText(record: Record<string, unknown>, key: string): string | null {
  const value = record[key];
  if (value === null || value === undefined) return null;
  if (typeof value !== 'string') throw new FormDocumentError(`${key} must be text or null`);
  return value;
}

function optionalNumber(record: Record<string, unknown>, key: string): number | null {
  const value = record[key];
  if (value === null || value === undefined) return null;
  if (typeof value !== 'number') throw new FormDocumentError(`${key} must be a number or null`);
  return value;
}

function flag(record: Record<string, unknown>, key: string): boolean {
  const value = record[key];
  return value === true;
}

function integer(record: Record<string, unknown>, key: string, fallback = 0): number {
  const value = record[key];
  return typeof value === 'number' ? value : fallback;
}

function parseField(raw: unknown): FormField {
  if (!isRecord(raw)) throw new FormDocumentError('a field must be an object');
  const bandsRaw = isRecord(raw.bands) ? raw.bands : {};
  const optionsRaw = Array.isArray(raw.options) ? raw.options : [];
  return {
    field_definition_id: text(raw, 'field_definition_id'),
    code: text(raw, 'code'),
    name: text(raw, 'name'),
    field_type: text(raw, 'field_type'),
    unit: optionalText(raw, 'unit'),
    decimals: optionalNumber(raw, 'decimals'),
    physical_min: optionalNumber(raw, 'physical_min'),
    physical_max: optionalNumber(raw, 'physical_max'),
    options: optionsRaw.filter(isRecord).map((option) => ({
      code: text(option, 'code'),
      label: text(option, 'label'),
    })),
    is_required: flag(raw, 'is_required'),
    requires_observation_on_fail: flag(raw, 'requires_observation_on_fail'),
    requires_photo_on_fail: flag(raw, 'requires_photo_on_fail'),
    limit_display: optionalText(raw, 'limit_display'),
    bands: {
      is_informational: flag(bandsRaw, 'is_informational'),
      green_min: optionalNumber(bandsRaw, 'green_min'),
      green_max: optionalNumber(bandsRaw, 'green_max'),
      warn_min: optionalNumber(bandsRaw, 'warn_min'),
      warn_max: optionalNumber(bandsRaw, 'warn_max'),
      criticality: optionalText(bandsRaw, 'criticality') ?? 'medium',
    },
    origin: optionalText(raw, 'origin') ?? 'template',
  };
}

function parseGroup(raw: unknown): FormGroup {
  if (!isRecord(raw)) throw new FormDocumentError('a group must be an object');
  const fields = Array.isArray(raw.fields) ? raw.fields : [];
  return {
    id: text(raw, 'id'),
    name: text(raw, 'name'),
    sort_order: integer(raw, 'sort_order'),
    min_photos: integer(raw, 'min_photos'),
    fields: fields.map(parseField),
  };
}

export function parseFormDocument(raw: unknown): FormDocument {
  if (!isRecord(raw)) throw new FormDocumentError('the document must be an object');
  const schemaVersion = raw.schema_version;
  if (schemaVersion !== SUPPORTED_SCHEMA_VERSION) {
    throw new FormDocumentError(`unsupported schema_version ${String(schemaVersion)}`);
  }
  const groups = Array.isArray(raw.groups) ? raw.groups : [];
  return {
    schema_version: schemaVersion,
    site_id: text(raw, 'site_id'),
    template_version_id: text(raw, 'template_version_id'),
    computed_at: text(raw, 'computed_at'),
    content_hash: text(raw, 'content_hash'),
    groups: groups.map(parseGroup).sort((a, b) => a.sort_order - b.sort_order),
  };
}

export function isKnownFieldType(type: string): type is KnownFieldType {
  return (KNOWN_FIELD_TYPES as readonly string[]).includes(type);
}
